<template>
  <div v-title="$route.meta.title"
       class="project-task-package-config">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/project-task-package/table' }">任务包及培训配置</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="project-task-package-config__card">
      <div class="project-task-package-config__card-header"
           @click="formExpanded = !formExpanded">
        <span>表单</span>
        <i :class="formExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
      </div>

      <el-form v-show="formExpanded"
               ref="form"
               v-loading="loading"
               :model="form"
               :rules="formRules"
               size="medium"
               label-width="0"
               class="project-task-package-config__body"
               @submit.native.prevent="onSubmit">
        <section class="project-task-package-config__section">
          <h3 class="project-task-package-config__section-title">基本信息</h3>

          <div class="project-task-package-config__row">
            <label class="project-task-package-config__label">项目</label>
            <div class="project-task-package-config__field">
              <div class="project-task-package-config__project-meta">
                <span class="project-task-package-config__project-name">{{ project.name || '-' }}</span>
                <span class="project-task-package-config__project-extra">
                  {{ project.number || '-' }} · ID {{ project.id || '-' }}
                </span>
              </div>
            </div>
          </div>

          <div class="project-task-package-config__row">
            <label class="project-task-package-config__label is-required">APP首页是否展示任务包</label>
            <div class="project-task-package-config__field">
              <el-form-item prop="app_task_package_display"
                            class="project-task-package-config__form-item">
                <el-radio-group v-model="form.app_task_package_display">
                  <el-radio v-for="item in opts.displayFormMaps"
                            :key="item.value"
                            :label="item.value">{{ item.label }}</el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </div>

          <template v-if="showPackageConfig">
            <div class="project-task-package-config__row">
              <label class="project-task-package-config__label is-required">首页任务包名称</label>
              <div class="project-task-package-config__field">
                <el-form-item prop="app_task_package_title"
                              class="project-task-package-config__form-item">
                  <el-input v-model="form.app_task_package_title"
                            maxlength="255"
                            show-word-limit
                            clearable
                            placeholder="请输入首页任务包名称"
                            class="project-task-package-config__title-input" />
                </el-form-item>
              </div>
            </div>

            <div v-if="isDisplayPartial(form.app_task_package_display)"
                 class="project-task-package-config__row">
              <label class="project-task-package-config__label is-required">白名单标签/标签组</label>
              <div class="project-task-package-config__field">
                <el-form-item prop="app_task_package_whitelist_tags"
                              class="project-task-package-config__form-item">
                  <remote-select
                    class="width-max project-task-package-config__title-input"
                    clearable
                    multiple
                    id-key="id"
                    label-key="tagname"
                    value-key="id"
                    placeholder="请输入标签或标签组关键字"
                    v-model="form.app_task_package_whitelist_tags"
                    :query-request="getUserTagTable"
                    :find-request="getUserTagDetail"
                  />
                </el-form-item>
              </div>
            </div>
          </template>
        </section>

        <section v-if="showPackageConfig"
                 class="project-task-package-config__section">
          <div class="project-task-package-config__section-head">
            <div>
              <h3 class="project-task-package-config__section-title project-task-package-config__section-title--inline">标签配置</h3>
              <p class="project-task-package-config__section-desc">
                同类别系统标签只能选一个，可取消勾选；系统标签与自定义标签合计最多选择 {{ maxSelected }} 个（当前已选 {{ selectedFlagCount }}）
              </p>
            </div>
            <div class="project-task-package-config__section-actions">
              <el-button type="primary"
                         plain
                         size="small"
                         icon="el-icon-setting"
                         @click="flagDialogVisible = true">维护自定义标签</el-button>
            </div>
          </div>

          <div class="project-task-package-config__row">
            <label class="project-task-package-config__label">系统标签1</label>
            <div class="project-task-package-config__field">
              <el-checkbox v-for="flag in systemFlagsByGroup.group1"
                           :key="'g1-' + flag.id"
                           class="project-task-package-config__flag-check"
                           v-model="form.system_group1_id"
                           :true-label="flag.id"
                           :false-label="null">{{ flag.name }}</el-checkbox>
            </div>
          </div>

          <div class="project-task-package-config__row">
            <label class="project-task-package-config__label">系统标签2</label>
            <div class="project-task-package-config__field">
              <el-checkbox v-for="flag in systemFlagsByGroup.group2"
                           :key="'g2-' + flag.id"
                           class="project-task-package-config__flag-check"
                           v-model="form.system_group2_id"
                           :true-label="flag.id"
                           :false-label="null">{{ flag.name }}</el-checkbox>
            </div>
          </div>

          <div class="project-task-package-config__row">
            <label class="project-task-package-config__label">系统标签3</label>
            <div class="project-task-package-config__field">
              <el-checkbox v-for="flag in systemFlagsByGroup.group3"
                           :key="'g3-' + flag.id"
                           class="project-task-package-config__flag-check"
                           v-model="form.system_group3_id"
                           :true-label="flag.id"
                           :false-label="null">{{ flag.name }}</el-checkbox>
            </div>
          </div>

          <div class="project-task-package-config__row">
            <label class="project-task-package-config__label">自定义标签</label>
            <div class="project-task-package-config__field">
              <el-checkbox-group v-model="form.custom_flag_ids"
                                 :max="5">
                <el-checkbox v-for="tag in customFlags"
                             :key="tag.id"
                             :label="tag.id">{{ tag.name }}</el-checkbox>
              </el-checkbox-group>
              <div v-if="!customFlags.length"
                   class="project-task-package-config__empty-hint">
                暂无自定义标签，请点击「维护自定义标签」添加
              </div>
            </div>
          </div>
        </section>

        <div class="project-task-package-config__footer">
          <span class="project-task-package-config__footer-tip">
            保存后立即对 APP 首页任务包展示生效
          </span>
          <div class="project-task-package-config__footer-actions">
            <el-button @click="$router.push({name: '任务包及培训配置'})">取消</el-button>
            <el-button type="primary"
                       native-type="submit"
                       :loading="saving">保存</el-button>
          </div>
        </div>
      </el-form>
    </div>

    <project-task-package-flag-dialog
      :visible.sync="flagDialogVisible"
      @changed="onFlagsChanged"
    />
  </div>
</template>

<script>
import ProjectTaskPackageFlagDialog from '../components/ProjectTaskPackageFlagDialog.vue'
import {
  consts,
  opts,
  isDisplayEnabled,
  isDisplayPartial,
} from '../project-task-package.constants'

export default {
  components: {ProjectTaskPackageFlagDialog},
  data () {
    return {
      consts,
      opts,
      isDisplayPartial,
      formExpanded: true,
      loading: false,
      saving: false,
      flagDialogVisible: false,
      project: {
        id: null,
        name: '',
        number: '',
      },
      form: {
        app_task_package_display: consts.DISPLAY_NONE,
        app_task_package_title: '',
        app_task_package_whitelist_tags: [],
        system_group1_id: null,
        system_group2_id: null,
        system_group3_id: null,
        custom_flag_ids: [],
      },
      systemFlags: [],
      customFlags: [],
      systemGroups: {
        group1: ['明访', '暗访'],
        group2: ['极速审核'],
        group3: ['简单', '适中', '挑战'],
      },
      maxSelected: 5,
      formRules: {
        app_task_package_title: [{validator: this.titleValidator, trigger: 'blur'}],
        app_task_package_whitelist_tags: [{validator: this.whitelistValidator, trigger: 'change'}],
      },
    }
  },
  computed: {
    showPackageConfig () {
      return isDisplayEnabled(this.form.app_task_package_display)
    },
    showTitle () {
      return this.showPackageConfig
    },
    systemFlagsByGroup () {
      const byName = {}
      const systemFlags = this.systemFlags || []
      systemFlags.forEach((f) => {
        byName[f.name] = f
      })
      const pick = (names) => (names || []).map((name) => byName[name]).filter(Boolean)
      return {
        group1: pick(this.systemGroups.group1),
        group2: pick(this.systemGroups.group2),
        group3: pick(this.systemGroups.group3),
      }
    },
    selectedFlagCount () {
      return this.buildSelectedFlagIds().length
    },
  },
  watch: {
    'form.app_task_package_display' (val) {
      if (!isDisplayPartial(val)) {
        this.form.app_task_package_whitelist_tags = []
      }
    },
  },
  created () {
    this.loadConfig()
  },
  methods: {
    ...request.map(['getUserTagTable', 'getUserTagDetail']),
    loadConfig () {
      this.loading = true
      const projectId = this.$route.params.projectId
      return request.common('getProjectTaskPackageConfig', {projectId}).then(({data}) => {
        this.project = {
          id: data.id,
          name: data.name,
          number: data.number,
        }
        const options = data.flag_options || {}
        this.systemFlags = options.system || []
        this.customFlags = options.custom || []
        this.systemGroups = options.system_groups || this.systemGroups
        this.maxSelected = options.max_selected || 5

        this.form.app_task_package_display = parseInt(
          data.app_task_package_display || consts.DISPLAY_NONE,
          10
        )
        this.form.app_task_package_title = data.app_task_package_title || ''
        this.form.app_task_package_whitelist_tags = Array.isArray(data.app_task_package_whitelist_tags)
          // remote-select / query_tag 的 id 为字符串，需与 option value 类型一致才能显示 label
          ? data.app_task_package_whitelist_tags.map((id) => String(id))
          : []

        const selected = Array.isArray(data.app_task_package_flags)
          ? data.app_task_package_flags.map((id) => id * 1)
          : []
        this.applySelectedFlags(selected)
      }).finally(() => {
        this.loading = false
      })
    },
    applySelectedFlags (selectedIds) {
      const idSet = {}
      const selected = selectedIds || []
      selected.forEach((id) => { idSet[Number(id)] = true })

      const pickGroupId = (flags) => {
        for (let i = 0; i < (flags || []).length; i++) {
          const id = Number(flags[i].id)
          if (idSet[id]) {
            return id
          }
        }
        return null
      }

      this.form.system_group1_id = pickGroupId(this.systemFlagsByGroup.group1)
      this.form.system_group2_id = pickGroupId(this.systemFlagsByGroup.group2)
      this.form.system_group3_id = pickGroupId(this.systemFlagsByGroup.group3)

      const systemIds = {}
      const groupKeys = ['group1', 'group2', 'group3']
      groupKeys.forEach((key) => {
        const flags = this.systemFlagsByGroup[key] || []
        flags.forEach((f) => {
          systemIds[Number(f.id)] = true
        })
      })
      this.form.custom_flag_ids = selected
        .map((id) => Number(id))
        .filter((id) => !systemIds[id])
    },
    buildSelectedFlagIds () {
      const ids = []
      const systemSelected = [
        this.form.system_group1_id,
        this.form.system_group2_id,
        this.form.system_group3_id,
      ]
      systemSelected.forEach((id) => {
        if (id) {
          ids.push(Number(id))
        }
      })
      const customIds = this.form.custom_flag_ids || []
      customIds.forEach((id) => {
        const num = Number(id)
        if (ids.indexOf(num) === -1) {
          ids.push(num)
        }
      })
      return ids
    },
    titleValidator (rule, value, callback) {
      if (this.showTitle && !(value || '').trim()) {
        callback(new Error('首页任务包名称不能为空'))
      } else {
        callback()
      }
    },
    whitelistValidator (rule, value, callback) {
      if (isDisplayPartial(this.form.app_task_package_display) && (!value || !value.length)) {
        callback(new Error('请选择白名单标签'))
      } else {
        callback()
      }
    },
    onFlagsChanged () {
      request.common('queryProjectTaskPackageFlag', {}).then(({data}) => {
        const payload = (data && data.data) ? data.data : data
        this.systemFlags = (payload && payload.system) || []
        this.customFlags = (payload && payload.custom) || []
      })
    },
    onSubmit () {
      const display = Number(this.form.app_task_package_display)
      const flagIds = this.buildSelectedFlagIds()
      if (display !== consts.DISPLAY_NONE && flagIds.length > this.maxSelected) {
        this.$message.error(`系统标签与自定义标签合计最多选择${this.maxSelected}个`)
        return
      }
      this.$refs.form.validate((valid) => {
        if (!valid) {
          return
        }
        this.saving = true
        const payload = {
          projectId: this.project.id,
          app_task_package_display: this.form.app_task_package_display,
          app_task_package_title: this.form.app_task_package_title,
          app_task_package_whitelist_tags: isDisplayPartial(display)
            ? this.form.app_task_package_whitelist_tags
            : [],
          app_task_package_flags: flagIds,
        }
        request.common('saveProjectTaskPackageConfig', payload).then(() => {
          this.$message.success('保存成功')
          this.$router.push({name: '任务包及培训配置'})
        }).finally(() => {
          this.saving = false
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.project-task-package-config {
  &__card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
    user-select: none;
    color: #606266;
    font-size: 14px;
    font-weight: 500;

    i {
      color: #909399;
    }
  }

  &__body {
    padding: 20px 24px 0;
  }

  &__section + &__section {
    margin-top: 24px;
  }

  &__section-title {
    margin: 0 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;

    &--inline {
      margin-bottom: 4px;
      padding-bottom: 0;
      border-bottom: none;
    }
  }

  &__section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
  }

  &__section-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    flex-shrink: 0;
  }

  &__section-desc {
    margin: 0;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
    font-weight: normal;
  }

  &__row {
    display: grid;
    grid-template-columns: minmax(160px, 18%) minmax(0, 1fr);
    gap: 16px;
    align-items: start;
    margin-bottom: 16px;
  }

  &__label {
    padding-top: 8px;
    color: #606266;
    font-size: 14px;
    line-height: 1.5;

    &.is-required::before {
      content: '*';
      color: #f56c6c;
      margin-right: 4px;
    }
  }

  &__field {
    min-width: 0;
  }

  &__form-item {
    margin-bottom: 0;
  }

  &__title-input {
    max-width: 480px;
  }

  &__project-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px 12px;
    padding-top: 8px;
  }

  &__project-name {
    color: #303133;
    font-size: 14px;
    font-weight: 500;
  }

  &__project-extra {
    color: #909399;
    font-size: 13px;
  }

  &__flag-check {
    margin-right: 16px;
  }

  &__empty-hint {
    color: #909399;
    font-size: 13px;
    padding: 8px 0 0;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px -24px 0;
    padding: 12px 24px;
    background: #f5f7fa;
    border-top: 1px solid #ebeef5;
    border-radius: 0 0 8px 8px;
  }

  &__footer-tip {
    color: #909399;
    font-size: 12px;
  }

  &__footer-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .project-task-package-config {
    &__row {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    &__label {
      padding-top: 0;
    }

    &__section-head {
      flex-direction: column;
      align-items: stretch;
    }

    &__section-actions {
      justify-content: flex-start;
    }

    &__footer {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/project-task-package/pages/ProjectTaskPackageConfig.vue